// ─────────────────────────────────────────────────────────────────────────────
// Getting the bytes. A device file is already here; a Blob is already here; a
// URL is fetched with a streaming reader so the shell can show real progress,
// and its failures are sorted into states the user can act on (CORS, expired
// link, sign-in) instead of one generic "failed".
// ─────────────────────────────────────────────────────────────────────────────

import { familyFromMeta } from './detect.js';
import { hostOf } from './format.js';
import type { FileViewerError, FileViewerSource, LoadedFile } from './types.js';

export const DEFAULT_MAX_LOCAL_BYTES = 2 * 1024 * 1024 * 1024;
export const DEFAULT_MAX_REMOTE_BYTES = 512 * 1024 * 1024;
const HEAD_BYTES = 8192;

export interface LoadOptions {
  signal: AbortSignal;
  maxLocalBytes?: number;
  maxRemoteBytes?: number;
  onProgress?: (loaded: number, total: number | null) => void;
  /** Bypass caches: a Refresh, which also wants to know whether anything changed. */
  revalidate?: boolean;
}

export class ViewerLoadError extends Error implements FileViewerError {
  readonly code: FileViewerError['code'];
  readonly status?: number;
  constructor(code: FileViewerError['code'], message: string, status?: number) {
    super(message);
    this.name = 'ViewerLoadError';
    this.code = code;
    this.status = status;
  }
}

export function toViewerError(e: unknown): FileViewerError {
  if (e instanceof ViewerLoadError) return { code: e.code, message: e.message, status: e.status };
  if (e instanceof DOMException && e.name === 'AbortError') return { code: 'aborted', message: 'aborted' };
  return { code: 'network', message: e instanceof Error ? e.message : String(e) };
}

export function displayName(source: FileViewerSource): string {
  if (source.kind === 'local') return source.file.name;
  if (source.kind === 'blob') return source.name;
  return source.name ?? decodeURIComponent(lastSegment(source.url)) ?? '';
}

function lastSegment(url: string): string {
  try {
    const u = new URL(url, typeof location !== 'undefined' ? location.href : undefined);
    return u.pathname.split('/').filter(Boolean).pop() ?? '';
  } catch {
    return url.split('?')[0].split('/').pop() ?? '';
  }
}

function needsHeaders(source: FileViewerSource): boolean {
  return source.kind === 'remote' && !!source.headers && Object.keys(source.headers).length > 0;
}

/** Media plays straight from a URL the browser can reach without headers. */
export function shouldStream(source: FileViewerSource): boolean {
  if (source.kind !== 'remote' || needsHeaders(source)) return false;
  if (source.stream === false) return false;
  const family = familyFromMeta(displayName(source), source.mime);
  return family === 'video' || family === 'audio';
}

export function linkFor(source: FileViewerSource): string | undefined {
  if (source.kind === 'local') return undefined;
  if (source.kind === 'blob') return source.linkUrl;
  if (source.linkUrl === null) return undefined;
  if (source.linkUrl) return source.linkUrl;
  return needsHeaders(source) ? undefined : source.url;
}

async function readHead(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.slice(0, HEAD_BYTES).arrayBuffer());
}

export async function loadSource(source: FileViewerSource, opts: LoadOptions): Promise<LoadedFile> {
  if (source.kind === 'local') {
    const limit = opts.maxLocalBytes ?? DEFAULT_MAX_LOCAL_BYTES;
    if (source.file.size > limit) throw new ViewerLoadError('too_large', `${source.file.size} > ${limit}`);
    const head = await readHead(source.file);
    return {
      blob: source.file,
      objectUrl: URL.createObjectURL(source.file),
      streamUrl: null,
      name: source.file.name,
      mime: source.file.type || '',
      size: source.file.size,
      origin: 'local',
      lastModified: source.file.lastModified ? new Date(source.file.lastModified).toISOString() : null,
      head,
      link: undefined
    };
  }

  if (source.kind === 'blob') {
    const head = await readHead(source.blob);
    return {
      blob: source.blob,
      objectUrl: URL.createObjectURL(source.blob),
      streamUrl: null,
      name: source.name,
      mime: source.mime || source.blob.type || '',
      size: source.blob.size,
      origin: 'blob',
      head,
      link: source.linkUrl
    };
  }

  const name = displayName(source);
  const link = linkFor(source);

  if (shouldStream(source)) {
    return {
      blob: null,
      objectUrl: null,
      streamUrl: source.url,
      name,
      mime: source.mime ?? '',
      size: source.size ?? 0,
      origin: 'remote',
      url: source.url,
      host: hostOf(source.url),
      head: new Uint8Array(0),
      link
    };
  }

  const limit = opts.maxRemoteBytes ?? DEFAULT_MAX_REMOTE_BYTES;
  if (source.size && source.size > limit) throw new ViewerLoadError('too_large', `${source.size} > ${limit}`);

  let res: Response;
  try {
    res = await fetch(source.url, {
      method: 'GET',
      headers: { accept: '*/*', ...(source.headers ?? {}) },
      credentials: source.credentials ?? 'same-origin',
      cache: opts.revalidate ? 'no-cache' : 'default',
      signal: opts.signal
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    // A cross-origin fetch the host refused surfaces as a TypeError with no
    // status. Same-origin failures with the same shape are the network.
    const cross = (() => {
      try { return new URL(source.url, location.href).origin !== location.origin; } catch { return false; }
    })();
    throw new ViewerLoadError(cross ? 'cors' : 'network', e instanceof Error ? e.message : 'fetch failed');
  }

  if (!res.ok) {
    const status = res.status;
    const signed = /[?&](X-Amz-Expires|X-Amz-Signature|Expires|sig|token)=/i.test(source.url);
    if ((status === 403 || status === 410) && signed) throw new ViewerLoadError('expired', `HTTP ${status}`, status);
    if (status === 401 || status === 403) throw new ViewerLoadError('auth', `HTTP ${status}`, status);
    if (status === 404) throw new ViewerLoadError('not_found', `HTTP ${status}`, status);
    throw new ViewerLoadError('http', `HTTP ${status}`, status);
  }

  const lengthHeader = res.headers.get('content-length');
  const total = lengthHeader ? Number(lengthHeader) : null;
  if (total && total > limit) throw new ViewerLoadError('too_large', `${total} > ${limit}`);

  const declared = res.headers.get('content-type') ?? source.mime ?? '';
  const disposition = res.headers.get('content-disposition') ?? '';
  const dispositionName = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1]
    ?? /filename="?([^";]+)"?/i.exec(disposition)?.[1];
  const finalName = source.name ?? (dispositionName ? safeDecode(dispositionName) : name);

  let blob: Blob;
  if (res.body && typeof res.body.getReader === 'function') {
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;
    opts.onProgress?.(0, total);
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        loaded += value.byteLength;
        if (loaded > limit) {
          await reader.cancel().catch(() => undefined);
          throw new ViewerLoadError('too_large', `${loaded} > ${limit}`);
        }
        opts.onProgress?.(loaded, total);
      }
    }
    blob = new Blob(chunks as BlobPart[], { type: declared.split(';')[0].trim() });
  } else {
    blob = await res.blob();
    opts.onProgress?.(blob.size, blob.size);
  }

  const head = await readHead(blob);
  return {
    blob,
    objectUrl: URL.createObjectURL(blob),
    streamUrl: null,
    name: finalName,
    mime: declared.split(';')[0].trim(),
    size: blob.size,
    origin: 'remote',
    url: source.url,
    host: hostOf(source.url),
    etag: res.headers.get('etag'),
    lastModified: res.headers.get('last-modified'),
    head,
    link
  };
}

function safeDecode(s: string): string {
  try { return decodeURIComponent(s); } catch { return s; }
}

/** Fetch a streamed media file in full, for Download on a file that was never downloaded. */
export async function fetchWhole(url: string, signal?: AbortSignal): Promise<Blob> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new ViewerLoadError('http', `HTTP ${res.status}`, res.status);
  return res.blob();
}

export function revoke(file: LoadedFile | null | undefined): void {
  if (file?.objectUrl) {
    try { URL.revokeObjectURL(file.objectUrl); } catch { /* already gone */ }
  }
}
